import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Table, Button, DatePicker, Row, Col, message, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { LoadingOutlined } from '@ant-design/icons';
import { t } from '../../../i18n';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { useCurrentDsp } from '../../../hooks/useCurrentDsp';
import { getUserRole } from '../../../utils/auth';
import {
  LIST_DSP_TASKS_BY_TASK_DATE,
  ListDspTasksByTaskDateResponse,
  ListDspTasksByTaskDateVariables,
  DspTaskTreeNode,
} from './graphql/listDspTasksByTaskDate';
import {
  LIST_TASK_DATES_WITH_PENDING_ASSIGN,
  ListTaskDatesWithPendingAssignResponse,
  ListTaskDatesWithPendingAssignVariables,
} from './graphql/listTaskDatesWithPendingAssign';
import { GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID } from '../tasks/graphql/queries/getAvailableDriversByWarehouseId';
import { Link } from 'react-router-dom';

/** 将 yyyy-MM-dd 转为 MMdd */
const formatTaskDateToMMdd = (dateStr: string): string => {
  if (!dateStr || dateStr.length < 10) return dateStr;
  return dateStr.slice(5, 7) + dateStr.slice(8, 10);
};

type DriverItem = { accountId: number; name: string; phone?: string; verificationStatus?: number };

const STATUS_KEYS: Record<number, string> = {
  20: '20',
  35: '35',
  40: '40',
  60: '60',
  70: '70',
  100: '100',
  110: '110',
  999: '999',
};

const formatStatusText = (status: number | null): string => {
  if (status == null) return '-';
  const key = STATUS_KEYS[status];
  if (!key) return String(status);
  return t(`delivery.routeAssign.status.${key}`, { defaultValue: String(status) });
};

/** 虚拟拆分段（前端状态） */
interface VirtualSegment {
  key: string;
  segmentIndex: number;
  start: number;
  end: number;
  total: number;
  driverId: number | null;
}

/** 表格行：API 数据或虚拟拆分行 */
type TableRow =
    | { type: 'api'; key: string; task: DspTaskTreeNode; level: number; isParent: boolean }
    | { type: 'virtual'; key: string; parentTaskId: number; parentName: string; segment: VirtualSegment; level: number };

const RouteAssignPage: React.FC = () => {
  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : 0;
  const userRole = getUserRole();
  const isDspManager = userRole === 'DSP_MANAGER';
  const { dspId: currentDspId } = useCurrentDsp();

  const [taskDate, setTaskDate] = useState<Dayjs>(dayjs().startOf('day'));
  /** parentTaskId -> VirtualSegment[] */
  const [splitState, setSplitState] = useState<Record<number, VirtualSegment[]>>({});
  /** 虚拟行司机选择 parentTaskId-segmentIndex -> driverId */
  const [virtualDriver, setVirtualDriver] = useState<Record<string, number | null>>({});
  /** 表格多选：选中的行 key 列表 */
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  /** API 行（父/子）司机覆盖：taskId -> driverId，用于未拆分父行选司机、撤回后子行重选 */
  const [apiDriverOverride, setApiDriverOverride] = useState<Record<string, number | null>>({});
  /** 已点击撤回的子任务 taskId 集合，撤回后司机展示为空、可重新选择、可删除 */
  const [withdrawnTaskIds, setWithdrawnTaskIds] = useState<Set<number>>(new Set());

  const queryVariables: ListDspTasksByTaskDateVariables | undefined = useMemo(() => {
    if (!warehouseId) return undefined;
    return {
      request: {
        warehouseId,
        taskDate: taskDate.format('YYYY-MM-DD'),
        dspId: isDspManager && currentDspId != null ? Number(currentDspId) : null,
      },
    };
  }, [warehouseId, taskDate, isDspManager, currentDspId]);

  const { data, loading } = useQuery<ListDspTasksByTaskDateResponse, ListDspTasksByTaskDateVariables>(
      LIST_DSP_TASKS_BY_TASK_DATE,
      {
        variables: queryVariables!,
        skip: !queryVariables,
        fetchPolicy: 'network-only',
      }
  );

  const { data: driversData } = useQuery(GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID, {
    variables: {
      warehouseId: warehouseId || undefined,
      dspId: isDspManager && currentDspId != null ? Number(currentDspId) : null,
    },
    skip: !warehouseId,
  });

  const pendingAssignVariables: ListTaskDatesWithPendingAssignVariables | undefined = useMemo(() => {
    if (!warehouseId) return undefined;
    return {
      request: {
        warehouseId,
        dspId: isDspManager && currentDspId != null ? Number(currentDspId) : null,
      },
    };
  }, [warehouseId, isDspManager, currentDspId]);

  const { data: pendingAssignData } = useQuery<
      ListTaskDatesWithPendingAssignResponse,
      ListTaskDatesWithPendingAssignVariables
  >(LIST_TASK_DATES_WITH_PENDING_ASSIGN, {
    variables: pendingAssignVariables!,
    skip: !pendingAssignVariables,
    fetchPolicy: 'network-only',
  });

  const pendingAssignDatesMMdd = useMemo(() => {
    const list = pendingAssignData?.listTaskDatesWithPendingAssign;
    if (!list || list.length === 0) return '';
    return list.map(formatTaskDateToMMdd).join('/');
  }, [pendingAssignData?.listTaskDatesWithPendingAssign]);

  const driverList: DriverItem[] = useMemo(() => {
    const list = (driversData?.getAvailableDriversByWarehouseId as DriverItem[] | undefined) ?? [];
    // 过滤掉 accountId 为 0 的占位司机，避免在下拉和已选值中显示“0”
    return list.filter((d) => d.accountId != null && d.accountId !== 0);
  }, [driversData]);

  const result = data?.listDspTasksByTaskDate;
  const totalUnassigned = result?.totalUnassignedParcels ?? 0;
  const totalAssigned = result?.totalAssignedParcels ?? 0;
  const taskList = result?.taskList ?? [];

  /** 构建扁平表格数据：API 行 + 虚拟拆分行 */
  const flatRows = useMemo((): TableRow[] => {
    const rows: TableRow[] = [];
    const pushTask = (task: DspTaskTreeNode, level: number, isParent: boolean) => {
      const key = `api-${task.taskId}`;
      rows.push({ type: 'api', key, task, level, isParent });
      const parentId = task.taskId ?? 0;
      const segments = splitState[parentId];
      if (segments && segments.length > 0) {
        segments.forEach((seg) => {
          rows.push({
            type: 'virtual',
            key: seg.key,
            parentTaskId: parentId,
            parentName: task.taskName ?? '',
            segment: seg,
            level: level + 1,
          });
        });
      }
      (task.subTaskList ?? []).forEach((child) => pushTask(child, level + 1, false));
    };
    taskList.forEach((task) => pushTask(task, 0, true));
    return rows;
  }, [taskList, splitState]);

  /** 已选线路数、总包裹数、指派司机人数（仅统计选中的 API 行；父行若已拆分则统计其下虚拟段司机） */
  const selectionSummary = useMemo(() => {
    const selected = flatRows.filter((r) => selectedRowKeys.includes(r.key));
    const apiRows = selected.filter((r): r is Extract<TableRow, { type: 'api' }> => r.type === 'api');
    const count = apiRows.length;
    let parcels = 0;
    const driverIds = new Set<number>();
    apiRows.forEach((r) => {
      const u = r.task.unassignedParcelCount ?? 0;
      const a = r.task.assignedParcelCount ?? 0;
      parcels += u + a;
      const taskId = r.task.taskId;
      if (r.isParent && taskId != null && (splitState[taskId]?.length ?? 0) > 0) {
        (splitState[taskId] ?? []).forEach((seg, idx) => {
          const did = virtualDriver[`${taskId}-${idx}`] ?? seg.driverId ?? null;
          if (did != null && did !== 0) driverIds.add(Number(did));
        });
      } else {
        const did = apiDriverOverride[String(taskId)] ?? r.task.driverId ?? null;
        if (did != null && did !== 0) driverIds.add(Number(did));
      }
    });
    return { count, parcels, drivers: driverIds.size };
  }, [flatRows, selectedRowKeys, apiDriverOverride, splitState, virtualDriver]);

  const handleConfirmAssign = () => {
    message.info(t('delivery.routeAssign.message.confirmAssignPlaceholder'));
  };

  const handleWithdraw = () => {
    message.info(t('delivery.routeAssign.message.withdrawPlaceholder'));
  };

  const handleWithdrawForTask = (taskId: number) => {
    setWithdrawnTaskIds((prev) => new Set(prev).add(taskId));
    setApiDriverOverride((prev) => ({ ...prev, [String(taskId)]: null }));
    message.info(t('delivery.routeAssign.message.withdrawPlaceholder'));
  };

  const handleDeleteApiChild = (taskId: number) => {
    message.info(t('delivery.routeAssign.message.withdrawPlaceholder')); // TODO: 调用后端删除接口
  };

  /** 均分父线路包裹为 n 段，保留每段原有 driverId（按索引），用于拆分/增删后刷新 */
  const buildSegmentsFromTotal = (
      parentId: number,
      total: number,
      n: number,
      preserveDriverIds: (number | null)[] = []
  ): VirtualSegment[] => {
    if (n <= 0 || total <= 0) return [];
    const baseSize = Math.floor(total / n);
    const remainder = total - baseSize * n;
    const newSegs: VirtualSegment[] = [];
    let start = 1;
    for (let i = 0; i < n; i++) {
      const segSize = baseSize + (i < remainder ? 1 : 0);
      const end = start + segSize - 1;
      newSegs.push({
        key: `v-${parentId}-${i}`,
        segmentIndex: i,
        start,
        end,
        total: segSize,
        driverId: preserveDriverIds[i] ?? null,
      });
      start = end + 1;
    }
    return newSegs;
  };

  const handleSplitLine = (parentTask: DspTaskTreeNode) => {
    const parentId = parentTask.taskId!;
    const total = parentTask.unassignedParcelCount ?? 0;
    if (total < 2) return;
    const newSegs = buildSegmentsFromTotal(parentId, total, 2, [null, null]);
    setSplitState((prev) => ({ ...prev, [parentId]: newSegs }));
  };

  /** 只允许编辑非最后一段的 end，第一段 start 固定为 1，最后一段 end 固定为 parentTotal */
  const updateSegmentEnd = (parentId: number, segmentIndex: number, endValue: number) => {
    const parentTask = taskList.find((t) => t.taskId === parentId);
    const total = parentTask?.unassignedParcelCount ?? 0;
    setSplitState((prev) => {
      const segs = (prev[parentId] ?? []).map((s) => ({ ...s }));
      const seg = segs[segmentIndex];
      if (!seg || segmentIndex === segs.length - 1) return prev;
      const start = seg.start;
      const end = Math.max(start, Math.min(endValue, total - (segs.length - 1 - segmentIndex)));
      seg.end = end;
      seg.total = end - start + 1;
      if (segmentIndex + 1 < segs.length) {
        const next = segs[segmentIndex + 1];
        next.start = end + 1;
        next.total = next.end - next.start + 1;
      }
      return { ...prev, [parentId]: segs };
    });
  };

  /** 永远从最后一条线路开始删；删完剩 1 条时直接收起，只展示父行 */
  const removeLastVirtualSegment = (parentId: number) => {
    const segs = splitState[parentId] ?? [];
    const lastIndex = segs.length - 1;
    let didCollapse = false;
    setSplitState((prev) => {
      const current = prev[parentId] ?? [];
      const newN = current.length - 1;
      if (newN <= 1) {
        didCollapse = true;
        const next = { ...prev };
        delete next[parentId];
        return next;
      }
      const parentTask = taskList.find((t) => t.taskId === parentId);
      const total = parentTask?.unassignedParcelCount ?? 0;
      const preserveDriverIds = current.slice(0, newN).map((s) => s.driverId);
      const newSegs = buildSegmentsFromTotal(parentId, total, newN, preserveDriverIds);
      return { ...prev, [parentId]: newSegs };
    });
    setVirtualDriver((p) => {
      const next = { ...p };
      if (didCollapse) {
        Object.keys(next).forEach((k) => {
          if (k.startsWith(`${parentId}-`)) delete next[k];
        });
      } else {
        delete next[`${parentId}-${lastIndex}`];
      }
      return next;
    });
  };

  const addVirtualSegment = (parentId: number) => {
    const parentTask = taskList.find((t) => t.taskId === parentId);
    const total = parentTask?.unassignedParcelCount ?? 0;
    const segs = splitState[parentId] ?? [];
    const maxN = Math.max(1, total);
    const n = Math.min(segs.length + 1, maxN);
    if (n <= segs.length) return;
    const preserveDriverIds = segs.map((s) => s.driverId);
    const newSegs = buildSegmentsFromTotal(parentId, total, n, preserveDriverIds);
    setSplitState((prev) => ({ ...prev, [parentId]: newSegs }));
  };

  const canSplit = (task: DspTaskTreeNode) =>
      [20, 35, 999].includes(task.status ?? 0) &&
      (!task.subTaskList || task.subTaskList.length === 0);
  const canWithdraw = (status: number | null) => [40, 60, 70].includes(status ?? 0);
  const hasSplit = (taskId: number) => (splitState[taskId]?.length ?? 0) > 0;
  const hasApiChildren = (task: DspTaskTreeNode) => (task.subTaskList?.length ?? 0) > 0;

  const renderStatus = (status: number | null) => {
    if (status == null) return '';
    const text = formatStatusText(status);
    if ([20, 35].includes(status)) {
      return (
          <span className="inline-block px-2 py-0.5 rounded bg-gray-200 text-black text-sm">{text}</span>
      );
    }
    if ([40, 70, 100].includes(status)) {
      return (
          <span className="inline-block px-2 py-0.5 rounded bg-green-500 text-white text-sm">{text}</span>
      );
    }
    if (status === 60) {
      return (
          <span className="inline-flex items-center gap-1">
          <span className="inline-block px-2 py-0.5 rounded bg-orange-500 text-white text-sm">{text}</span>
          <LoadingOutlined className="text-orange-500" spin />
        </span>
      );
    }
    if ([110, 999].includes(status)) {
      return (
          <span className="inline-block px-2 py-0.5 rounded bg-red-400 text-black text-sm">{text}</span>
      );
    }
    return text;
  };

  const renderDriverSelect = (
      value: number | null | undefined,
      onChange: (id: number | null) => void,
      disabled?: boolean,
      excludeIds: number[] = []
  ) => {
    const currentId = value != null ? Number(value) : null;
    const excludeSet = new Set(excludeIds.map((id) => Number(id)));

    return (
        <Select
            style={{ width: 280 }}
            placeholder={t('delivery.routes.label.searchPlaceholder')}
            // 司机 id 为 0 或 '0' 时视为未选择，显示占位文案
            value={value != null && value !== 0 && value !== ('0' as any) ? String(value) : undefined}
            loading={!driversData}
            disabled={disabled}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const d = driverList.find((x) => String(x.accountId) === option?.value);
              if (!d) return false;
              const search = input.toLowerCase();
              return (d.phone ?? '').toLowerCase().includes(search) || (d.name ?? '').toLowerCase().includes(search);
            }}
            onChange={(v) => onChange(v != null && v !== '' ? Number(v) : null)}
        >
          {driverList
              .filter((d) => {
                const id = Number(d.accountId);
                // 同一父线路拆成多条子线路时，多个子线路不能指派同一个司机：
                // 排除已在兄弟子线路中使用的司机，但保留当前行已选中的司机
                if (currentId != null && id === currentId) return true;
                return !excludeSet.has(id);
              })
              .map((d) => (
                  <Select.Option key={d.accountId} value={String(d.accountId)}>
                    <span style={{ fontSize: 14 }}>{d.phone ?? ''}</span>
                    <span style={{ fontSize: 12, color: '#666', marginLeft: 4 }}>({d.name})</span>
                    {d.verificationStatus === 2 && (
                        <span style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                {t('delivery.routes.label.driverVerified')}
              </span>
                    )}
                    {d.verificationStatus != null && d.verificationStatus !== 2 && (
                        <span style={{ fontSize: 12, color: '#faad14', marginLeft: 8 }}>
                {t('delivery.routes.label.driverUnverifiedShort')}
              </span>
                    )}
                  </Select.Option>
              ))}
        </Select>
    );
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: t('delivery.routeAssign.table.status'),
      key: 'status',
      width: 160,
      render: (_, row) => {
        if (row.type === 'virtual') return '';
        return renderStatus(row.task.status);
      },
    },
    {
      title: t('delivery.routeAssign.table.route'),
      key: 'route',
      width: 180,
      render: (_, row) => {
        const paddingLeft = row.level * 20;
        if (row.type === 'virtual') {
          return <span style={{ paddingLeft }}>{`${row.parentName}-${row.segment.segmentIndex + 1}`}</span>;
        }
        return <span style={{ paddingLeft }}>{row.task.taskName ?? ''}</span>;
      },
    },
    {
      title: t('delivery.routeAssign.table.unassignedParcels'),
      key: 'unassigned',
      width: 200,
      render: (_, row) => {
        if (row.type === 'virtual') {
          const s = row.segment;
          const segs = splitState[row.parentTaskId] ?? [];
          const isLast = row.segment.segmentIndex === segs.length - 1;
          const parentTask = taskList.find((t) => t.taskId === row.parentTaskId);
          const parentTotal = parentTask?.unassignedParcelCount ?? 0;
          return (
              <span className="flex items-center gap-1 text-blue-600">
              <span className="inline-block w-8 text-right">{s.start}</span>
              <span>-</span>
                {isLast ? (
                    <span className="inline-block w-8">{s.end}</span>
                ) : (
                    <input
                        type="number"
                        className="w-14 border rounded px-1 text-sm text-blue-600"
                        value={s.end}
                        min={s.start}
                        max={parentTotal}
                        onChange={(e) => updateSegmentEnd(row.parentTaskId, row.segment.segmentIndex, parseInt(e.target.value, 10) || s.start)}
                    />
                )}
                <span>（{s.total}）</span>
            </span>
          );
        }
        const v = row.task.unassignedParcelCount;
        return <span className="text-blue-600">{v != null ? String(v) : ''}</span>;
      },
    },
    {
      title: t('delivery.routeAssign.table.assignedParcels'),
      key: 'assigned',
      width: 120,
      render: (_, row) => {
        if (row.type === 'virtual') return <span className="text-green-600">0</span>;
        const v = row.task.assignedParcelCount;
        return <span className="text-green-600">{v != null ? String(v) : ''}</span>;
      },
    },
    {
      title: t('delivery.routeAssign.table.dsp'),
      key: 'dsp',
      width: 120,
      render: (_, row) => {
        if (row.type === 'virtual') {
          const parent = taskList.find((t) => t.taskId === row.parentTaskId);
          return parent?.dspName ?? '';
        }
        return row.task.dspName ?? '';
      },
    },
    {
      title: t('delivery.routeAssign.table.driver'),
      key: 'driver',
      width: 300,
      render: (_, row) => {
        if (row.type === 'virtual') {
          const key = `${row.parentTaskId}-${row.segment.segmentIndex}`;
          const value = virtualDriver[key] ?? row.segment.driverId;
          const segs = splitState[row.parentTaskId] ?? [];
          // 收集同一父线路下其他子线路的已选司机，用于排除
          const siblingDriverIds: number[] = segs
              .map((seg, idx) => {
                if (idx === row.segment.segmentIndex) return null;
                const k = `${row.parentTaskId}-${idx}`;
                const v = virtualDriver[k] ?? seg.driverId;
                return v != null && v !== 0 ? Number(v) : null;
              })
              .filter((v): v is number => v != null);

          return renderDriverSelect(
              value ?? null,
              (id) => setVirtualDriver((p) => ({ ...p, [key]: id })),
              false,
              siblingDriverIds
          );
        }
        if (row.isParent && hasSplit(row.task.taskId!)) return '';
        const taskId = row.task.taskId!;
        const withdrawn = withdrawnTaskIds.has(taskId);
        const value = apiDriverOverride[String(taskId)] ?? (withdrawn ? null : row.task.driverId) ?? null;
        return renderDriverSelect(
            value,
            (id) => setApiDriverOverride((p) => ({ ...p, [String(taskId)]: id })),
            false
        );
      },
    },
    {
      title: t('delivery.routeAssign.table.actions'),
      key: 'actions',
      width: 260,
      render: (_, row) => {
        if (row.type === 'virtual') {
          const segs = splitState[row.parentTaskId] ?? [];
          const isLast = row.segment.segmentIndex === segs.length - 1;
          const parentTask = taskList.find((t) => t.taskId === row.parentTaskId);
          const parentTotal = parentTask?.unassignedParcelCount ?? 0;
          const canAdd = segs.length < Math.max(1, parentTotal);
          return (
              <span className="flex items-center gap-1">
              {isLast && segs.length > 1 && (
                  <Button type="default" size="small" danger onClick={() => removeLastVirtualSegment(row.parentTaskId)}>
                    {t('delivery.routeAssign.action.delete')}
                  </Button>
              )}
                {isLast && (
                    <Button type="default" size="small" disabled={!canAdd} onClick={() => addVirtualSegment(row.parentTaskId)}>
                      {t('delivery.routeAssign.action.add')}
                    </Button>
                )}
            </span>
          );
        }
        const taskId = row.task.taskId!;
        const isApiChild = !row.isParent;
        const canDeleteApiChild = isApiChild && (withdrawnTaskIds.has(taskId) || !row.task.driverId || row.task.driverId === 0);

        const showSplit = row.isParent && canSplit(row.task);
        const showWithdraw = canWithdraw(row.task.status);
        return (
            <span className="flex items-center gap-1">
            {row.isParent && (
                <Button
                    type="default"
                    size="small"
                    disabled={!showSplit}
                    onClick={() => showSplit && handleSplitLine(row.task)}
                >
                  {t('delivery.routeAssign.action.splitLine')}
                </Button>
            )}
              {showWithdraw && (
                  <Button
                      type="default"
                      size="small"
                      onClick={() => (isApiChild ? handleWithdrawForTask(taskId) : handleWithdraw())}
                  >
                    {t('delivery.routeAssign.action.withdraw')}
                  </Button>
              )}
              {isApiChild && canDeleteApiChild && (
                  <Button type="default" size="small" danger onClick={() => handleDeleteApiChild(taskId)}>
                    {t('delivery.routeAssign.action.delete')}
                  </Button>
              )}
          </span>
        );
      },
    },
  ];

  return (
      <div style={{ padding: 24 }}>
        <div className="border-l-4 border-indigo-600 pl-2 text-xl font-medium mb-4">
          {t('delivery.routeAssign.title')}
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-4">
          <span className="text-gray-600">{t('delivery.routeAssign.taskDate')}</span>
          <DatePicker
              value={taskDate}
              onChange={(d) => d && setTaskDate(d)}
              format="YYYY-MM-DD"
          />
          <Button type="primary" className="bg-indigo-600" onClick={handleConfirmAssign}>
            {t('delivery.routeAssign.confirmAssign')}
          </Button>
        </div>
        {pendingAssignDatesMMdd && (
            <div className="mb-4 text-red-600 text-sm">
              {t('delivery.routeAssign.pendingAssignHint', { dates: pendingAssignDatesMMdd })}
            </div>
        )}

        <Row gutter={16} className="mb-6">
          <Col span={12}>
            <div className="rounded p-4 bg-blue-50 border border-blue-200 text-center h-[100px] flex flex-col justify-center">
              <div className="text-gray-900 font-bold text-base mb-1">{t('delivery.routeAssign.overviewUnassigned')}</div>
              <div className="text-blue-600 font-medium text-lg">{totalUnassigned}</div>
            </div>
          </Col>
          <Col span={12}>
            <div className="rounded p-4 bg-green-50 border border-green-200 text-center h-[100px] flex flex-col justify-center">
              <div className="text-gray-900 font-bold text-base mb-1">{t('delivery.routeAssign.overviewAssigned')}</div>
              <div className="text-green-600 font-medium text-lg">{totalAssigned}</div>
              <div className="text-gray-400 text-xs mt-2">
                {t('delivery.routeAssign.historyHint')}{' '}
                <Link to="/wms/routes-history" className="text-blue-600 hover:underline">{t('delivery.routeAssign.historyLink')}</Link>
              </div>
            </div>
          </Col>
        </Row>

        <div className="mb-2 text-gray-500 text-sm">
          {t('delivery.routeAssign.selectedSummary', {
            count: selectionSummary.count,
            parcels: selectionSummary.parcels,
            drivers: selectionSummary.drivers,
          })}
        </div>

        <Table<TableRow>
            rowKey="key"
            columns={columns}
            dataSource={flatRows}
            loading={loading}
            pagination={false}
            size="small"
            rowClassName={(row) => {
              if (row.type === 'virtual') return 'bg-gray-50';
              if (row.type === 'api' && (row.task.unassignedParcelCount ?? 0) === 0) return 'bg-green-50';
              return '';
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
              getCheckboxProps: (row: TableRow) => ({
                disabled:
                    row.type === 'virtual' || (row.type === 'api' && (row.task.unassignedParcelCount ?? 0) === 0),
              }),
            }}
        />
      </div>
  );
};

export default RouteAssignPage;
